import { useState, useEffect } from 'react';
import { finance, hrms, getFileUrl } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import { SearchableSelect } from '../../components/common/SearchableSelect';
import { theme } from '../../theme';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const YEARS  = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

const inr    = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inrPdf = (v) => `Rs. ${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function MonthlyReport() {
  const [employees, setEmployees]     = useState([]);
  const [employeeId, setEmployeeId]   = useState('');
  const [month, setMonth]             = useState(new Date().getMonth() + 1);
  const [year, setYear]               = useState(new Date().getFullYear());
  const [report, setReport]           = useState(null);
  const [loading, setLoading]         = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pdfLoading, setPdfLoading]   = useState(false);
  const [error, setError]             = useState('');

  useEffect(() => {
    hrms.getEmployees({ limit: 500 }).then((r) => {
      const d = r.data;
      setEmployees(Array.isArray(d) ? d : d.items || []);
    }).catch(() => {});
  }, []);

  const empOptions = employees.map(e => ({
    value: String(e.id),
    label: `${e.full_name}${e.emp_code ? ` (${e.emp_code})` : ''}`,
  }));

  const selectedEmp = employees.find(e => String(e.id) === String(employeeId));

  const fetchReport = async () => {
    if (!employeeId) { setError('Please select an employee.'); return; }
    setLoading(true); setError('');
    try {
      const r = await finance.getMonthlyReport({ employee_id: employeeId, month, year });
      setReport(r.data);
    } catch {
      setError('Failed to fetch report. No data may be available for the selected period.');
      setReport(null);
    } finally { setLoading(false); }
  };

  const handleDownloadExcel = async () => {
    if (!employeeId) { setError('Please select an employee.'); return; }
    setDownloading(true); setError('');
    try {
      const r = await finance.downloadMonthlyClaimsReport({ employee_id: employeeId, month, year });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Claims_${selectedEmp?.emp_code || employeeId}_${MONTHS[month - 1]}_${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download Excel report.');
    } finally { setDownloading(false); }
  };

  const handleDownloadPdf = async () => {
    if (!report) return;
    setPdfLoading(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      // A4 portrait B&W — 210×297mm, 14mm margins, usable width 182mm
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const PW  = 210;
      const ML  = 14;
      const MR  = 14;
      const UW  = PW - ML - MR;  // 182mm

      const empName    = selectedEmp?.full_name || report.employee_name || '';
      const empCode    = selectedEmp?.emp_code  || report.employee_code || '';
      const monthLabel = MONTHS[month - 1];

      // All text/lines in pure black
      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(0, 0, 0);

      // ── Top border line ───────────────────────────────────────────────────
      doc.setLineWidth(0.8);
      doc.line(ML, 8, PW - MR, 8);

      // Company name
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('BonTon Softwares Pvt Ltd', ML, 14);

      // Report title
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Monthly Claims Report  |  ${monthLabel} ${year}`, ML, 20);

      // Generated date — right aligned
      doc.setFontSize(7);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, PW - MR, 14, { align: 'right' });

      // ── Employee name — large, bold, prominent ────────────────────────────
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text(empName, ML, 32);

      // Emp code
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(empCode, ML, 38);

      // Divider line below employee info
      doc.setLineWidth(0.4);
      doc.line(ML, 42, PW - MR, 42);

      // ── Summary boxes (plain bordered, no fill) ───────────────────────────
      const BOX_W = (UW - 6) / 3;
      const summaries = [
        { label: 'Total Claims',    value: String(report.total_claims ?? 0) },
        { label: 'Total Submitted', value: inrPdf(report.total_submitted) },
        { label: 'Total Approved',  value: inrPdf(report.total_approved) },
      ];
      doc.setLineWidth(0.3);
      summaries.forEach((s, i) => {
        const x = ML + i * (BOX_W + 3);
        doc.rect(x, 46, BOX_W, 16);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(s.value, x + BOX_W / 2, 56, { align: 'center' });
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(s.label, x + BOX_W / 2, 60, { align: 'center' });
      });

      // ── Table ─────────────────────────────────────────────────────────────
      const tableRows = (report.claims || []).map((c, i) => [
        i + 1,
        c.expense_date || '—',
        c.project_name || '—',
        c.claim_type   || '—',
        c.description  || '—',
        inrPdf(c.submitted_amount),
        inrPdf(c.approved_amount),
      ]);

      autoTable(doc, {
        startY: 67,
        head: [['#', 'Date', 'Project', 'Claim Type', 'Description', 'Submitted Amt', 'Approved Amt']],
        body: tableRows,
        foot: [['', '', '', '', 'TOTAL', inrPdf(report.total_submitted), inrPdf(report.total_approved)]],
        theme: 'grid',
        headStyles:  { fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
        footStyles:  { fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 9 },
        bodyStyles:  { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          0: { cellWidth: 8,  halign: 'center' },
          1: { cellWidth: 22, halign: 'center' },
          2: { cellWidth: 30 },
          3: { cellWidth: 22 },
          4: { cellWidth: 56 },
          5: { cellWidth: 22, halign: 'right' },
          6: { cellWidth: 22, halign: 'right' },
        },
        margin: { left: ML, right: MR },
      });

      // ── Signature row ─────────────────────────────────────────────────────
      const finalY = doc.lastAutoTable.finalY + 14;
      const SIG_W  = (UW - 9) / 4;
      const sigs    = ['PREPARED BY', 'CHECKED BY', 'APPROVED BY', 'AUTHORIZED BY'];
      const sigVals = [empName, '', '', ''];
      doc.setLineWidth(0.3);
      sigs.forEach((label, i) => {
        const x = ML + i * (SIG_W + 3);
        doc.rect(x, finalY, SIG_W, 20);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(label, x + SIG_W / 2, finalY + 6, { align: 'center' });
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(sigVals[i], x + SIG_W / 2, finalY + 14, { align: 'center' });
      });

      // Bottom border
      doc.setLineWidth(0.4);
      doc.line(ML, finalY + 24, PW - MR, finalY + 24);

      // ── Receipt pages (3 per page) ─────────────────────────────────────────
      const receiptItems = (report.claims || []).filter(c => c.bill_file_path);

      if (receiptItems.length > 0) {
        // Helper: fetch image path → base64 data URL
        const toDataUrl = (path) => {
          const url = getFileUrl(path);
          if (!url) return Promise.resolve(null);
          return fetch(url)
            .then(r => { if (!r.ok) throw new Error(r.status); return r.blob(); })
            .then(blob => new Promise(resolve => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror  = () => resolve(null);
              reader.readAsDataURL(blob);
            }))
            .catch(() => null);
        };

        const ITEMS_PER_PAGE = 3;
        const PAGE_H = 297;
        const PH     = PAGE_H - 14 - 14; // usable page height (top+bottom margins)
        const SLOT_H = Math.floor(PH / ITEMS_PER_PAGE); // mm per receipt slot
        const IMG_MAX_W = UW;
        const IMG_MAX_H = SLOT_H - 14; // leave room for label

        for (let i = 0; i < receiptItems.length; i++) {
          const item = receiptItems[i];
          const posInPage = i % ITEMS_PER_PAGE;

          if (posInPage === 0) {
            doc.addPage();
            // Page header
            doc.setLineWidth(0.8);
            doc.line(ML, 8, PW - MR, 8);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text('Receipts / Bills', ML, 14);
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.text(`${empName}  |  ${monthLabel} ${year}`, PW - MR, 14, { align: 'right' });
            doc.setLineWidth(0.3);
            doc.line(ML, 16, PW - MR, 16);
          }

          const slotY = 20 + posInPage * SLOT_H;

          // Label
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          const label = `#${i + 1}  ${item.description || ''}  |  ${item.expense_date || ''}  |  ${inrPdf(item.submitted_amount)}`;
          doc.text(label, ML, slotY + 5, { maxWidth: UW });

          // Image
          const dataUrl = await toDataUrl(item.bill_file_path);
          if (dataUrl) {
            // Detect format from data URL prefix
            const fmt = dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
            // Get natural dimensions via Image element
            const imgDims = await new Promise(resolve => {
              const img = new Image();
              img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
              img.onerror = () => resolve(null);
              img.src = dataUrl;
            });
            if (imgDims) {
              const aspect = imgDims.w / imgDims.h;
              let iW = IMG_MAX_W;
              let iH = iW / aspect;
              if (iH > IMG_MAX_H) { iH = IMG_MAX_H; iW = iH * aspect; }
              doc.addImage(dataUrl, fmt, ML, slotY + 9, iW, iH);
            }
          } else {
            doc.setFontSize(7);
            doc.setTextColor(180, 0, 0);
            doc.text('[Receipt image unavailable]', ML, slotY + 18);
            doc.setTextColor(0, 0, 0);
          }
        }
      }

      doc.save(`Claims_Report_${empCode}_${monthLabel}_${year}.pdf`);
    } catch (e) {
      console.error(e);
      setError('Failed to generate PDF.');
    } finally { setPdfLoading(false); }
  };

  const sel = { padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none' };

  return (
    <div style={{ maxWidth: '1000px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Monthly Claims Report</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Only approved claims are included</p>
        </div>
        {report && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button variant="outlineGray" onClick={() => window.print()}>🖨️ Print</Button>
            <Button variant="outlineGray" onClick={handleDownloadExcel} loading={downloading}>⬇ Excel</Button>
            <Button variant="primary" onClick={handleDownloadPdf} loading={pdfLoading}>⬇ Download PDF</Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.4rem' }}>Employee *</label>
            <SearchableSelect
              options={empOptions}
              value={employeeId || ''}
              onChange={(val) => setEmployeeId(val || '')}
              placeholder="Type to search employee..."
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.4rem' }}>Month</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={sel}>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.4rem' }}>Year</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={sel}>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <Button variant="primary" onClick={fetchReport} loading={loading}>Generate Report</Button>
        </div>
        {error && <p style={{ color: theme.danger, fontSize: '0.875rem', marginTop: '0.75rem' }}>⚠️ {error}</p>}
      </Card>

      {/* Report */}
      {report && (
        <div id="print-report">
          {/* Header */}
          <div style={{ background: theme.dark, color: theme.white, borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.2rem' }}>BonTon Softwares Pvt Ltd</h2>
              <p style={{ margin: 0, color: '#9CA3AF', fontSize: '0.875rem' }}>Monthly Claims Report — {MONTHS[month - 1]} {year}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#9CA3AF' }}>Employee</p>
              <p style={{ margin: 0, fontWeight: 700, fontSize: '1rem' }}>{selectedEmp?.full_name || report.employee_name}</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#9CA3AF' }}>{selectedEmp?.emp_code || report.employee_code}</p>
            </div>
          </div>

          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            {[
              { label: 'Total Claims',     value: report.total_claims ?? 0 },
              { label: 'Total Submitted',  value: inr(report.total_submitted) },
              { label: 'Total Approved',   value: inr(report.total_approved) },
            ].map((s, i) => (
              <div key={i} style={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1rem', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <p style={{ fontSize: '1.4rem', fontWeight: 700, color: theme.primary, margin: 0 }}>{s.value}</p>
                <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.25rem 0 0' }}>{s.label}</p>
              </div>
            ))}
          </div>

          {/* Claims Table — one row per expense item */}
          <Card title="Expense Items Detail" padding="0">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC' }}>
                    {['S.No', 'Expense Date', 'Project', 'Claim Type', 'Description', 'Submitted Amt', 'Approved Amt', 'Status'].map(h => (
                      <th key={h} style={{ padding: '0.75rem 1rem', textAlign: h.includes('Amt') ? 'right' : 'left', fontWeight: 600, color: theme.textMuted, borderBottom: `2px solid ${theme.border}`, fontSize: '0.72rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(report.claims || []).length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2.5rem', color: theme.textMuted }}>No approved claims found for this period</td></tr>
                  ) : (report.claims || []).map((c, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '0.75rem 1rem', color: theme.textMuted }}>{i + 1}</td>
                      <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>{c.expense_date || '—'}</td>
                      <td style={{ padding: '0.75rem 1rem', color: theme.textMuted }}>{c.project_name || '—'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <span style={{ background: '#EFF6FF', color: '#1D4ED8', fontSize: '0.72rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: '0.25rem' }}>{c.claim_type || '—'}</span>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, maxWidth: 200 }}>{c.description || '—'}</td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>{inr(c.submitted_amount)}</td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, color: theme.success }}>{inr(c.approved_amount)}</td>
                      <td style={{ padding: '0.75rem 1rem' }}><Badge status={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#F8FAFC', fontWeight: 700 }}>
                    <td colSpan={5} style={{ padding: '0.875rem 1rem', textAlign: 'right', color: theme.text, fontSize: '0.875rem' }}>TOTAL</td>
                    <td style={{ padding: '0.875rem 1rem', textAlign: 'right', color: theme.text }}>{inr(report.total_submitted)}</td>
                    <td style={{ padding: '0.875rem 1rem', textAlign: 'right', color: theme.success, fontSize: '1rem' }}>{inr(report.total_approved)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default MonthlyReport;
